import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import type { WireArticleBlock, WireArticleFull } from '../../api/types'
import type { NavMethod } from '../../api/services/articleService'
import { metricsApiClient } from '../../api/http/wireApiClient'
import { SourceListContext, type SourceEntry } from './common/SourceListContext'
import { ArticleArtifactsContext } from './common/ArticleArtifactsContext'
import { VisualEngagementProvider } from './common/VisualEngagementContext'
import type { ArticleActivityEntry, ArticleVisualActivityEntry } from '../metrics/types'
import { dateToApi, nDaysAgoStr, todayStr } from '../metrics/helpers'
import { ArticleHeader }    from './article/ArticleHeader'
import { ArticleThumbnail } from './article/ArticleThumbnail'
import { ArticleToolbar }   from './article/ArticleToolbar'
import { ArticleAdminRail } from './article/ArticleAdminRail'
import { RevisionNoticesBanner } from './article/RevisionNoticesBanner'
import { ARTICLE_BLOCK_REGISTRY } from './blocks/registry'
import { TextboxBlock } from './blocks/TextboxBlock'

type VisualMetricRollup = {
  visualId: string
  visualType: string
  uniqueReaders: number
  methodReaderCounts: Record<string, number>
}

type VisualMetricOption = {
  id: string
  label: string
  method?: string
}

type VisualStoryTrack = {
  primary: VisualMetricOption[]
  orthogonal: VisualMetricOption[]
}

type ChartColors = {
  fg: string
  fgSecondary: string
  line: string
  tooltipBg: string
  tooltipFg: string
}

function getBlockVisualId(block: WireArticleBlock): string | null {
  if (block.type === 'visual') return block.artifactId
  if (block.type === 'video') return block.artifactId || block.id
  if (block.type === 'audio') return block.artifactId || block.id
  if (block.type === 'photo') return block.photo.artifactId || block.id
  if (block.type === 'photo-group') return block.id
  if (block.type === 'photo-comparison') return block.id
  return null
}

function rollupVisualRows(rows: ArticleVisualActivityEntry[]): Map<string, VisualMetricRollup> {
  const byVisual = new Map<string, { visualType: string; readers: Set<string>; methodReaders: Map<string, Set<string>> }>()
  for (const row of rows) {
    const existing = byVisual.get(row.visualId)
    if (existing) {
      existing.readers.add(row.clientDn)
      if (!existing.methodReaders.has(row.interactionMethod)) {
        existing.methodReaders.set(row.interactionMethod, new Set())
      }
      existing.methodReaders.get(row.interactionMethod)?.add(row.clientDn)
      continue
    }
    const methodReaders = new Map<string, Set<string>>()
    methodReaders.set(row.interactionMethod, new Set([row.clientDn]))
    byVisual.set(row.visualId, {
      visualType: row.visualType,
      readers: new Set([row.clientDn]),
      methodReaders,
    })
  }

  const result = new Map<string, VisualMetricRollup>()
  for (const [visualId, v] of byVisual) {
    const methodReaderCounts: Record<string, number> = {}
    for (const [method, readers] of v.methodReaders) methodReaderCounts[method] = readers.size
    result.set(visualId, {
      visualId,
      visualType: v.visualType,
      uniqueReaders: v.readers.size,
      methodReaderCounts,
    })
  }
  return result
}

function getVisualLabel(block: WireArticleBlock): string | null {
  if (block.type === 'video') return 'Video'
  if (block.type === 'audio') return 'Audio'
  if (block.type === 'visual') return 'Visual'
  if (block.type === 'photo') return 'Image'
  if (block.type === 'photo-group') return 'Gallery'
  if (block.type === 'photo-comparison') return 'Comparison'
  return null
}

function getVisualStoryTrack(visualType: string): VisualStoryTrack {
  if (visualType === 'VIDEO') {
    return {
      primary: [
        { id: 'inView', label: 'Saw the Video', method: 'VIEW_ENTER' },
        { id: 'plays', label: 'Played It', method: 'PLAY' },
        { id: 'completes', label: 'Finished It', method: 'COMPLETE' },
      ],
      orthogonal: [
        { id: 'fullscreenOpens', label: 'Opened Fullscreen', method: 'FULLSCREEN_OPEN' },
      ],
    }
  }

  if (visualType === 'AUDIO') {
    return {
      primary: [
        { id: 'inView', label: 'Saw the Audio Block', method: 'VIEW_ENTER' },
        { id: 'plays', label: 'Played It', method: 'PLAY' },
        { id: 'completes', label: 'Finished It', method: 'COMPLETE' },
      ],
      orthogonal: [
        { id: 'seeks', label: 'Seeked', method: 'SEEK' },
      ],
    }
  }

  if (visualType === 'IMAGE' || visualType === 'LEGACY_VISUAL') {
    return {
      primary: [
        { id: 'inView', label: 'Saw the Image', method: 'VIEW_ENTER' },
        { id: 'clicks', label: 'Clicked It', method: 'CLICK' },
        { id: 'lightboxOpens', label: 'Opened the Viewer', method: 'LIGHTBOX_OPEN' },
      ],
      orthogonal: [
        { id: 'hovers', label: 'Hovered', method: 'HOVER_OVER' },
      ],
    }
  }

  if (visualType === 'GALLERY') {
    return {
      primary: [
        { id: 'inView', label: 'Saw the Gallery', method: 'VIEW_ENTER' },
        { id: 'drags', label: 'Moved Through It', method: 'DRAG' },
        { id: 'clicks', label: 'Clicked It', method: 'CLICK' },
      ],
      orthogonal: [
        { id: 'hovers', label: 'Hovered', method: 'HOVER_OVER' },
      ],
    }
  }

  if (visualType === 'COMPARISON') {
    return {
      primary: [
        { id: 'inView', label: 'Saw the Gallery', method: 'VIEW_ENTER' },
        { id: 'drags', label: 'Used the Comparison Slider', method: 'DRAG' },
      ],
      orthogonal: [],
    }
  }

  return {
    primary: [
      { id: 'inView', label: 'Saw the Visual', method: 'VIEW_ENTER' },
      { id: 'hovers', label: 'Hovered', method: 'HOVER_OVER' },
      { id: 'clicks', label: 'Clicked It', method: 'CLICK' },
    ],
    orthogonal: [],
  }
}

function getVisualStepReaders(visual: VisualMetricRollup, step: VisualMetricOption): number {
  if (!step.method) return visual.uniqueReaders
  return visual.methodReaderCounts[step.method] ?? 0
}

function resolveChartColors(): ChartColors {
  if (typeof window === 'undefined') {
    return {
      fg: '#e5e7eb',
      fgSecondary: '#9ca3af',
      line: '#374151',
      tooltipBg: '#111827',
      tooltipFg: '#f9fafb',
    }
  }
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg: get('--gray-12') || '#111111',
    fgSecondary: get('--gray-11') || '#6b7280',
    line: get('--gray-6') || '#e5e7eb',
    tooltipBg: get('--gray-12') || '#111111',
    tooltipFg: get('--gray-1') || '#fcfcfc',
  }
}

interface ArticleRendererProps {
  article: WireArticleFull
  trackVisualEngagement?: boolean
  navMethod?: NavMethod
  pageId?: string
  adminMode?: boolean
  canToggleAdminMode?: boolean
  onToggleAdminMode?: () => void
}

/**
 * Pure article renderer — no editor chrome, no selection state.
 * Used by:
 *   - ArticlePreview dialog (article-reviewer toolbar Eye button)
 *   - Future reader route /articles/:id
 *
 * Layout model (from customer mockup):
 *   - Outer container: max-w-5xl, centered — the "page" width
 *   - 'body' blocks: constrained to max-w-3xl, centered within the outer container
 *   - 'full' blocks: span the full outer container width (thumbnail, wide visuals)
 */
export function ArticleRenderer({
  article,
  trackVisualEngagement = false,
  navMethod,
  pageId,
  adminMode = false,
  canToggleAdminMode = false,
  onToggleAdminMode,
}: ArticleRendererProps) {
  const [avgScrollDepth, setAvgScrollDepth] = useState<number | null>(null)
  const [visualRows, setVisualRows] = useState<ArticleVisualActivityEntry[]>([])
  const [articleUniqueReaders, setArticleUniqueReaders] = useState(0)
  const [fullDepthReaders, setFullDepthReaders] = useState(0)

  useEffect(() => {
    if (!adminMode) {
      setVisualRows([])
      setArticleUniqueReaders(0)
      setFullDepthReaders(0)
      return
    }
    const params = {
      from: dateToApi(nDaysAgoStr(90), false),
      to: dateToApi(todayStr(), true),
    }
    metricsApiClient
      .get<ArticleVisualActivityEntry[]>(`/metrics/article/visuals/activity/per-article/${article.id}`, { params })
      .then(({ data }) => setVisualRows(data ?? []))
      .catch(() => setVisualRows([]))

    metricsApiClient
      .get<ArticleActivityEntry[]>(`/metrics/article/activity/per-article/${article.id}`, { params })
      .then(({ data }) => {
        const rows = data ?? []
        const uniqueReaders = new Set(rows.map(r => r.clientDn))
        const reachedBottomReaders = new Set(rows.filter(r => r.maxScrollYIndex >= 100).map(r => r.clientDn))
        setArticleUniqueReaders(uniqueReaders.size)
        setFullDepthReaders(reachedBottomReaders.size)
      })
      .catch(() => {
        setArticleUniqueReaders(0)
        setFullDepthReaders(0)
      })
  }, [adminMode, article.id])

  // Build source entry list once for the citation context
  const sources = useMemo<SourceEntry[]>(() => {
    const sourceListBlock = (article.blocks ?? []).find(
      (b): b is Extract<WireArticleBlock, { type: 'source-list' }> => b.type === 'source-list'
    )
    return sourceListBlock?.entries ?? []
  }, [article.blocks])

  const bodyWidthClass = 'mx-auto max-w-3xl'
  const visualMetricsById = useMemo(() => rollupVisualRows(visualRows), [visualRows])
  const visualUniqueReaders = useMemo(() => new Set(visualRows.map(r => r.clientDn)).size, [visualRows])
  const articleReadersDenominator = articleUniqueReaders > 0 ? articleUniqueReaders : visualUniqueReaders
  const fullDepthPct = articleUniqueReaders > 0 ? Math.round((fullDepthReaders / articleUniqueReaders) * 1000) / 10 : 0
  const chartColors = useMemo(resolveChartColors, [])

  function renderBlock(block: WireArticleBlock): React.ReactNode {
    const entry = ARTICLE_BLOCK_REGISTRY[block.type]
    if (!entry) return null

    const { component: Component, layout } = entry

    const rendered =
      block.type === 'textbox'
        ? <TextboxBlock block={block as Extract<WireArticleBlock, { type: 'textbox' }>} renderBlock={renderBlock} />
        : <Component block={block} />

    if (layout === 'full') {
      return rendered
    }

    // 'body' — constrain to reading column, centered within the outer container
    return (
      <div className={bodyWidthClass}>
        {rendered}
      </div>
    )
  }

  if (article.workflow.stage === 'recalled') {
    return (
      <div className="mx-auto w-full max-w-5xl px-8 py-10">
        <div className="mx-auto max-w-3xl rounded-lg border border-error-line bg-error-fill px-8 py-12 text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-error-fg">Notice</p>
          <h1 className="font-zodiak mt-3 text-2xl font-bold text-fg">This Article Has Been Recalled</h1>
          <p className="mt-3 text-sm text-fg-secondary">
            This article has been removed from publication by the editorial staff and is no longer available.
          </p>
        </div>
      </div>
    )
  }

  const content = (
    <ArticleArtifactsContext.Provider value={article.artifacts ?? []}>
    <SourceListContext.Provider value={sources}>
      <div className={adminMode ? 'w-full px-8 py-10' : 'mx-auto w-full max-w-5xl px-8 py-10'}>
        <div className={adminMode ? 'grid gap-8 transition-[grid-template-columns,gap] duration-500 ease-out lg:grid-cols-[minmax(0,56rem)_minmax(22rem,1fr)]' : 'space-y-6'}>
          <div className="space-y-6 transition-transform duration-500 ease-out will-change-transform lg:translate-x-0">

          {/* Revision notices — body-width, above headline */}
          {article.revisions && article.revisions.length > 0 && (
            <div className="mx-auto max-w-3xl">
              <RevisionNoticesBanner revisions={article.revisions} />
            </div>
          )}

          {/* Article header — always body-width */}
          <div className="mx-auto max-w-3xl">
            <ArticleHeader article={article} />
          </div>

          {/* Toolbar — body-width, between header and thumbnail */}
          <div className="mx-auto max-w-3xl">
            <ArticleToolbar
              article={article}
              adminMode={adminMode}
              canToggleAdminMode={canToggleAdminMode}
              onToggleAdminMode={onToggleAdminMode}
            />
          </div>

          {/* Thumbnail — full container width */}
          <ArticleThumbnail article={article} />

          {/* Block loop */}
          {(() => {
            const blocks = article.blocks ?? []
            const rendered = blocks
              .map(block => ({ block, node: renderBlock(block) }))
              .filter((entry): entry is { block: WireArticleBlock; node: React.ReactNode } => Boolean(entry.node))

            // API depth is a percentage (0-100). Convert to an index within rendered blocks.
            const depthIdx = adminMode && avgScrollDepth !== null && rendered.length > 0
              ? Math.round((Math.min(Math.max(avgScrollDepth, 0), 100) / 100) * (rendered.length - 1))
              : null

            return (
              <>
                {rendered.map(({ block, node }, idx) => {
              const visualId = getBlockVisualId(block)
              const visualLabel = getVisualLabel(block)
              const m = visualId ? visualMetricsById.get(visualId) : undefined
              const visualType = (m?.visualType ?? 'UNKNOWN').toUpperCase()
              const storyTrack = getVisualStoryTrack(visualType)
              const funnelData = m
                ? storyTrack.primary.map(step => ({
                    name: step.label,
                    value: getVisualStepReaders(m, step),
                  }))
                : []
              const hasFunnelData = funnelData.some(d => d.value > 0)
              const maxValue = Math.max(articleReadersDenominator, ...funnelData.map(d => d.value), 1)
              const inViewReaders = funnelData[0]?.value ?? 0
              const nextStepReaders = funnelData[1]?.value ?? 0
              const finalStepReaders = funnelData[2]?.value ?? 0
              const orthogonalReaders = m && storyTrack.orthogonal.length > 0
                ? getVisualStepReaders(m, storyTrack.orthogonal[0])
                : 0
              const nextStepFromViewPct = inViewReaders > 0 ? Math.round((nextStepReaders / inViewReaders) * 1000) / 10 : 0
              const finalFromNextPct = nextStepReaders > 0 ? Math.round((finalStepReaders / nextStepReaders) * 1000) / 10 : 0
              const orthogonalFromNextPct = nextStepReaders > 0 ? Math.round((orthogonalReaders / nextStepReaders) * 1000) / 10 : 0
              const hasFinalPrimaryStep = storyTrack.primary.length > 2
              const firstTransitionLabel = `Saw the Visual → ${storyTrack.primary[1]?.label ?? 'Next Step'}`
              const secondTransitionLabel = hasFinalPrimaryStep
                ? `${storyTrack.primary[1]?.label ?? 'Next Step'} → ${storyTrack.primary[2]?.label ?? 'Final Step'}`
                : ''

              const funnelOption = {
                backgroundColor: 'transparent',
                textStyle: { color: chartColors.fg },
                tooltip: {
                  trigger: 'item' as const,
                  backgroundColor: chartColors.tooltipBg,
                  borderColor: chartColors.line,
                  textStyle: { color: chartColors.tooltipFg, fontSize: 11 },
                  formatter: (params: { dataIndex: number }) => {
                    const current = funnelData[params.dataIndex]
                    const previous = params.dataIndex > 0 ? funnelData[params.dataIndex - 1] : null
                    const fromPrevious = previous && previous.value > 0
                      ? Math.round((current.value / previous.value) * 1000) / 10
                      : null
                    return [
                      `<b>${current.name}</b>`,
                      `Readers: <b>${current.value.toLocaleString()}</b>`,
                      previous
                        ? `From prior step: <b>${fromPrevious?.toFixed(1) ?? '0.0'}%</b>`
                        : `Of article readers: <b>${articleReadersDenominator > 0 ? Math.round((current.value / articleReadersDenominator) * 1000) / 10 : 0}%</b>`,
                    ].filter(Boolean).join('<br/>')
                  },
                },
                series: [{
                  type: 'funnel' as const,
                  left: '2%',
                  top: 8,
                  bottom: 8,
                  width: '62%',
                  min: 0,
                  max: maxValue,
                  sort: 'none' as const,
                  gap: 4,
                  label: {
                    show: true,
                    position: 'right' as const,
                    color: chartColors.fg,
                    formatter: (params: { dataIndex: number }) => {
                      const current = funnelData[params.dataIndex]
                      const pctTotal = articleReadersDenominator > 0 ? Math.round((current.value / articleReadersDenominator) * 1000) / 10 : 0
                      return `${current.name}\n${current.value.toLocaleString()} readers · ${pctTotal.toFixed(1)}% of total`
                    },
                  },
                  labelLine: {
                    lineStyle: { color: chartColors.line },
                  },
                  itemStyle: {
                    borderColor: chartColors.line,
                    borderWidth: 1,
                  },
                  data: funnelData,
                }],
              }

              const showVisualCard = adminMode && Boolean(visualId) && Boolean(visualLabel)

              return (
                <div key={block.id} className={showVisualCard ? 'relative' : ''}>
                  {node}
                  {showVisualCard && (
                    <aside className="hidden xl:block absolute left-[calc(100%+2rem)] top-0 w-[22rem] rounded-lg border border-line bg-surface p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-[11px] font-semibold text-fg">{visualLabel}</p>
                        <span className="text-[10px] uppercase tracking-wider text-fg-secondary">{m?.visualType ?? 'N/A'}</span>
                      </div>
                      {hasFunnelData ? (
                        <ReactECharts option={funnelOption} style={{ height: 220 }} />
                      ) : (
                        <div className="rounded border border-dashed border-line bg-canvas px-2 py-3 text-center text-[10px] text-fg-secondary">
                          No funnel data yet
                        </div>
                      )}
                      <div className="mt-2 divide-y divide-line border-t border-line">
                        <div className="flex items-start justify-between gap-3 py-2">
                          <div>
                            <p className="text-[11px] text-fg">Saw the Visual</p>
                            <p className="text-[10px] text-fg-secondary">
                              {articleReadersDenominator > 0 ? Math.round((inViewReaders / articleReadersDenominator) * 1000) / 10 : 0}% of article readers
                            </p>
                          </div>
                          <p className="text-sm font-semibold tabular-nums text-fg">{inViewReaders.toLocaleString()}</p>
                        </div>
                        <div className="flex items-start justify-between gap-3 py-2">
                          <div>
                            <p className="text-[11px] text-fg">{firstTransitionLabel}</p>
                            <p className="text-[10px] text-fg-secondary">{nextStepReaders.toLocaleString()} of {inViewReaders.toLocaleString()} continued</p>
                          </div>
                          <p className="text-sm font-semibold tabular-nums text-fg">{nextStepFromViewPct.toFixed(1)}%</p>
                        </div>
                        {hasFinalPrimaryStep && (
                          <div className="flex items-start justify-between gap-3 py-2">
                            <div>
                              <p className="text-[11px] text-fg">{secondTransitionLabel}</p>
                              <p className="text-[10px] text-fg-secondary">{finalStepReaders.toLocaleString()} of {nextStepReaders.toLocaleString()} reached the end</p>
                            </div>
                            <p className="text-sm font-semibold tabular-nums text-fg">{finalFromNextPct.toFixed(1)}%</p>
                          </div>
                        )}
                        {storyTrack.orthogonal.length > 0 && (
                          <div className="flex items-start justify-between gap-3 py-2">
                            <div>
                              <p className="text-[11px] text-fg">{storyTrack.orthogonal[0].label}</p>
                              <p className="text-[10px] text-fg-secondary">Share of readers who continued</p>
                            </div>
                            <p className="text-sm font-semibold tabular-nums text-fg">{orthogonalFromNextPct.toFixed(1)}%</p>
                          </div>
                        )}
                      </div>
                    </aside>
                  )}
                  {depthIdx !== null && idx === depthIdx && (
                    <div className="pointer-events-none relative left-1/2 z-20 my-3 w-screen -translate-x-1/2">
                      <div className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 bg-accent-primary-solid/70" />
                      <span className="relative ml-auto mr-4 block w-fit rounded border border-accent-primary-solid bg-accent-primary-fill px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-accent-primary-fg shadow-sm">
                        avg scroll depth · {avgScrollDepth}%
                      </span>
                    </div>
                  )}
                </div>
              )
                })}
                {adminMode && articleUniqueReaders > 0 && (
                  <div className="pointer-events-none relative left-1/2 z-20 mt-3 w-screen -translate-x-1/2">
                    <div className="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 bg-accent-primary-solid/50" />
                    <span className="relative ml-auto mr-4 block w-fit rounded border border-accent-primary-solid bg-accent-primary-fill px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-accent-primary-fg shadow-sm">
                      100% scroll depth · {fullDepthPct}% ({fullDepthReaders.toLocaleString()} of {articleUniqueReaders.toLocaleString()} readers)
                    </span>
                  </div>
                )}
              </>
            )
          })()}

          </div>

          {adminMode && (
            <aside className="mt-2 min-h-0 overflow-y-auto px-2 transition-opacity duration-500 ease-out opacity-100 lg:sticky lg:top-16 lg:min-h-[calc(100vh-5rem)]">
              <div className="transition-all duration-500 ease-out translate-y-0 opacity-100">
                <ArticleAdminRail articleId={article.id} onAvgScrollDepth={setAvgScrollDepth} />
              </div>
            </aside>
          )}
        </div>
      </div>
    </SourceListContext.Provider>
    </ArticleArtifactsContext.Provider>
  )

  if (!trackVisualEngagement) return content

  return (
    <VisualEngagementProvider articleId={article.id} navMethod={navMethod} pageId={pageId}>
      {content}
    </VisualEngagementProvider>
  )
}
